import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/layout/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ClipboardCheck, Package, RefreshCw, Search, ShieldCheck, Truck, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = {
  pending: "status-warning",
  assigned: "status-neutral",
  in_transit: "status-active",
  delivered: "status-active",
  cancelled: "status-error",
};

const MarketplacePage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoad, setActionLoad] = useState(null);
  const [signature, setSignature] = useState("");
  const [note, setNote] = useState("");
  const [driverId, setDriverId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");
  const [activeTab, setActiveTab] = useState("all");
  const [signingLoadId, setSigningLoadId] = useState("");
  const [driverKycStatus, setDriverKycStatus] = useState(null);

  const fetchLoads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/marketplace/loads`);
      setLoads(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    if (role !== "dispatcher") return;
    try {
      const response = await axios.get(`${API}/users/drivers`);
      setDrivers(response.data || []);
    } catch (_) {
      // Optional list for dispatcher assignment workflow.
    }
  }, [role]);

  const upsertLoad = (nextLoad) => {
    setLoads((prev) => {
      const idx = prev.findIndex((l) => l.id === nextLoad.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...nextLoad };
        return copy;
      }
      return [nextLoad, ...prev];
    });
  };

  useEffect(() => {
    fetchLoads();
    fetchDrivers();
    if (role === "driver") {
      axios.get(`${API}/kyc/me`).then((res) => setDriverKycStatus(res.data?.status || "not_submitted")).catch(() => {});
    }
  }, [fetchDrivers, fetchLoads, role]);

  useEffect(() => {
    const token = localStorage.getItem("eagle_token");
    if (!token) return;
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol).replace(/\/$/, "")
      : `${wsProtocol}//${window.location.host}`;

    const socket = new WebSocket(`${wsHost}/api/ws/marketplace?token=${encodeURIComponent(token)}`);
    socket.onmessage = (event) => {
      if (event.data === "pong") return;
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "marketplace_event" && payload.shipment) {
          upsertLoad(payload.shipment);
        }
      } catch (_) {
        // ignore malformed frames
      }
    };

    const ping = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) socket.send("ping");
    }, 25000);

    return () => {
      clearInterval(ping);
      socket.close();
    };
  }, []);

  const availableDrivers = useMemo(
    () => drivers.filter((d) => d.driver_status === "available" || d.id === actionLoad?.driver_id),
    [drivers, actionLoad?.driver_id],
  );

  const visibleLoads = useMemo(() => {
    const base = role === "driver"
      ? loads.filter((l) => (l.status === "pending" && !l.driver_id) || l.driver_id === user?.id)
      : loads;

    const q = search.trim().toLowerCase();
    let filtered = base.filter((l) => {
      const statusOk = statusFilter === "all" || l.status === statusFilter;
      const queryOk =
        !q ||
        String(l.tracking_number || "").toLowerCase().includes(q) ||
        String(l.origin || "").toLowerCase().includes(q) ||
        String(l.destination || "").toLowerCase().includes(q) ||
        String(l.cargo_type || "").toLowerCase().includes(q);

      if (!statusOk || !queryOk) return false;

      if (activeTab === "my_accepted") {
        if (role === "driver") return l.driver_id === user?.id;
        return l.status === "assigned" || l.status === "in_transit";
      }
      if (activeTab === "pending_signature") {
        return l.status === "assigned" || l.status === "in_transit";
      }
      return true;
    });

    filtered = filtered.sort((a, b) => {
      if (sortBy === "pickup_asc") return String(a.pickup_date || "").localeCompare(String(b.pickup_date || ""));
      if (sortBy === "pickup_desc") return String(b.pickup_date || "").localeCompare(String(a.pickup_date || ""));
      if (sortBy === "updated_asc") return String(a.updated_at || "").localeCompare(String(b.updated_at || ""));
      return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
    });

    return filtered;
  }, [loads, role, user?.id, search, statusFilter, sortBy, activeTab]);

  const openAcceptDialog = (load) => {
    setActionLoad(load);
    setSignature(user?.name ? `${user.name} acceptance` : "");
    setNote("");
    setDriverId(load.driver_id || "");
  };

  const handleAccept = async () => {
    if (!actionLoad) return;
    if (!signature.trim() || signature.trim().length < 5) {
      toast.error("Signature must be at least 5 characters");
      return;
    }
    if (role === "dispatcher" && !driverId) {
      toast.error("Select a driver");
      return;
    }

    setSubmitting(true);
    try {
      const payload = { signature: signature.trim(), note: note.trim() };
      if (role === "dispatcher") payload.driver_id = driverId;
      const response = await axios.post(`${API}/marketplace/shipments/${actionLoad.id}/accept`, payload);
      upsertLoad(response.data);
      toast.success("Load acceptance signed");
      setActionLoad(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not sign acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignBol = async (load) => {
    setSigningLoadId(load.id);
    try {
      const bolRes = await axios.get(`${API}/bol/shipment/${load.id}`);
      const bol = bolRes.data;
      if (!bol?.id) {
        toast.error("No BOL found for this load yet.");
        return;
      }

      const signatureType = role === "driver" ? "carrier" : "shipper";
      const alreadySigned =
        (signatureType === "carrier" && bol.signed_by_carrier) ||
        (signatureType === "shipper" && bol.signed_by_shipper);
      if (alreadySigned) {
        toast.info(`BOL already signed as ${signatureType}`);
        return;
      }

      await axios.post(`${API}/bol/${bol.id}/sign`, { signature_type: signatureType });
      toast.success(`BOL signed as ${signatureType}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to sign BOL");
    } finally {
      setSigningLoadId("");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={role} />
      <main className="lg:ml-64 p-4 lg:p-8">
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight">Marketplace</h1>
              <p className="text-muted-foreground mt-1">
                Live loads stream. Drivers and dispatchers can accept and sign assignments in real time.
              </p>
            </div>
            <Button variant="outline" onClick={fetchLoads} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              {role === "driver" && driverKycStatus !== "verified" && (
                <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                  KYC is currently <span className="font-semibold capitalize">{String(driverKycStatus || "not_submitted").replaceAll("_", " ")}</span>. You cannot accept loads until it is verified.
                </div>
              )}
              <div className="flex flex-col gap-3 lg:flex-row">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    placeholder="Search tracking, origin, destination, cargo"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full lg:w-44">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full lg:w-48">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Latest Updated</SelectItem>
                    <SelectItem value="updated_asc">Oldest Updated</SelectItem>
                    <SelectItem value="pickup_asc">Pickup Date (Earliest)</SelectItem>
                    <SelectItem value="pickup_desc">Pickup Date (Latest)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant={activeTab === "all" ? "default" : "outline"} onClick={() => setActiveTab("all")}>All Loads</Button>
                <Button size="sm" variant={activeTab === "my_accepted" ? "default" : "outline"} onClick={() => setActiveTab("my_accepted")}>My Accepted</Button>
                <Button size="sm" variant={activeTab === "pending_signature" ? "default" : "outline"} onClick={() => setActiveTab("pending_signature")}>Pending Signature</Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleLoads.map((load) => {
              const mine = load.driver_id && load.driver_id === user?.id;
              const canAccept =
                role === "dispatcher" ||
                (role === "driver" && (!load.driver_id || mine) && load.status !== "delivered" && load.status !== "cancelled");
              const driverBlockedByKyc = role === "driver" && driverKycStatus !== "verified";
              return (
                <Card key={load.id} className="card-hover">
                  <CardHeader>
                    <CardTitle className="flex items-start justify-between gap-2 text-lg">
                      <span className="font-mono text-sm">{load.tracking_number}</span>
                      <Badge className={statusBadge[load.status] || "status-neutral"}>
                        {String(load.status || "").replaceAll("_", " ")}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{load.origin} to {load.destination}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-muted-foreground">Cargo:</span> {load.cargo_type}</p>
                      <p><span className="text-muted-foreground">Weight:</span> {load.weight} lbs</p>
                      <p><span className="text-muted-foreground">Pickup:</span> {load.pickup_date}</p>
                      <p><span className="text-muted-foreground">Delivery:</span> {load.delivery_date}</p>
                    </div>

                    <div className="rounded-md border border-border/60 p-2 text-xs text-muted-foreground">
                      <p><span className="font-semibold text-foreground">Assigned Driver:</span> {load.driver_id || "Not assigned"}</p>
                      <p><span className="font-semibold text-foreground">Last Action:</span> {load.marketplace_last_action?.action || "created"}</p>
                      <p><span className="font-semibold text-foreground">Signed By:</span> {load.marketplace_last_action?.by_name || "-"}</p>
                      <p><span className="font-semibold text-foreground">Action Time:</span> {load.marketplace_last_action?.signed_at ? new Date(load.marketplace_last_action.signed_at).toLocaleString() : "-"}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {canAccept && (
                        <Dialog open={actionLoad?.id === load.id} onOpenChange={(open) => !open && setActionLoad(null)}>
                          <DialogTrigger asChild>
                            <Button
                              className="w-full bg-primary hover:bg-primary/90"
                              onClick={() => openAcceptDialog(load)}
                              disabled={driverBlockedByKyc}
                            >
                              {role === "dispatcher" ? <UserCheck className="w-4 h-4 mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
                              {role === "dispatcher" ? "Assign + Sign" : driverBlockedByKyc ? "KYC Required" : "Accept + Sign"}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-lg">
                            <DialogHeader>
                              <DialogTitle className="font-heading flex items-center gap-2">
                                <ClipboardCheck className="w-5 h-5 text-primary" />
                                Marketplace Acceptance
                              </DialogTitle>
                              <DialogDescription>
                                This records a signed assignment action for audit and operations.
                              </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                              {role === "dispatcher" && (
                                <div className="space-y-2">
                                  <Label>Select Driver</Label>
                                  <Select value={driverId} onValueChange={setDriverId}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select a driver" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableDrivers.map((driver) => (
                                        <SelectItem key={driver.id} value={driver.id}>
                                          {driver.name} ({String(driver.driver_status || "unknown").replaceAll("_", " ")})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              <div className="space-y-2">
                                <Label>Signature</Label>
                                <Input
                                  value={signature}
                                  onChange={(e) => setSignature(e.target.value)}
                                  placeholder="Type your full name/signature"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Operational Note (optional)</Label>
                                <Input
                                  value={note}
                                  onChange={(e) => setNote(e.target.value)}
                                  placeholder="Dispatch note or acceptance note"
                                />
                              </div>

                              <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleAccept} disabled={submitting}>
                                {submitting ? "Signing..." : "Sign Acceptance"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                      {(role === "driver" || role === "dispatcher") && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleSignBol(load)}
                          disabled={signingLoadId === load.id}
                        >
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          {signingLoadId === load.id ? "Signing..." : "Sign BOL"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {visibleLoads.length === 0 && !loading && (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No marketplace loads are available right now.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default MarketplacePage;
